import { InvoiceStatus } from '@/Components/Invoicing/InvoiceStatus';
import { SortableWorklistColumn, WorklistColumn, WorklistFilterProperty } from '@/Pages/GenericWorklist/Logic/WorklistProperties';
import type Status from '../Shared/Status';
import { JustificationFilterOptions } from './JustificationFilterOptions';
import type ReportParticipantType from './ReportParticipantType';

interface Worklist {
    Params: WorklistParams;
    View: WorklistViewParams;
}

interface WorklistParams {
    Filter?: WorklistFilterParams;
    Sorting?: WorklistSortingParam[];
    Pagination: WorklistPaginationParams;
}

type WorklistFilterViewParam = WorklistViewParam<WorklistFilterProperty>;
type WorklistColumnViewParam = WorklistViewParam<WorklistColumn>;

interface WorklistViewParam<T> {
    ID: T;
    Visible: boolean;
}

interface WorklistViewParams {
    // NOTE: Order in the array defines the order of filters/columns in the view!
    Filters: WorklistFilterViewParam[];
    Columns: WorklistColumnViewParam[];
}

interface WorklistFilterParams {
    SiteIDs?: number[];
    RoomIDs?: number[];
    PatientFirstName?: string;
    PatientLastName?: string;
    DoB?: Date;
    ExternalID?: string;
    PatientNationalNumber?: string;
    Language?: string;
    ProcedureStatuses?: Status[];
    OrderStatuses?: Status[];
    PatientStatuses?: number[];
    PatientOrigins?: number[];
    PatientLanguageIDs?: number[];
    VisitNumber?: string;
    UnitOfCare?: string;
    UrgencyIDs?: number[];
    LabelsToIncludeIDs?: number[];
    LabelsToExcludeIDs?: number[];
    DateRangePreset?: string;
    From?: Date | null;
    Until?: Date | null;
    AccessionNumber?: string;
    Radiologists?: { ID: number; FullName: string }[];
    Technicians?: { ID: number; FullName: string }[];
    ProcedureDescription?: string;
    ModalityIDs?: number[];
    ProcedureCodes?: string[];
    OrderNumber?: string;
    RequestingPhysicianFirstName?: string;
    RequestingPhysicianLastName?: string;
    InvoiceStatuses?: InvoiceStatus[];
    ReadyForInvoicing?: boolean;
    ReportLocked?: boolean;
    ReportAvailable?: boolean;
    ReportStatuses?: number[];
    ReportModifiedBy?: { ID: number; FullName: string }[];
    ReportCreatedDateRangePreset?: string;
    ReportCreatedFrom?: Date | null;
    ReportCreatedUntil?: Date | null;
    ReportModifiedDateRangePreset?: string;
    ReportModifiedFrom?: Date | null;
    ReportModifiedUntil?: Date | null;
    Justification?: JustificationFilterOptions;
    ReportParticipants?: { ID: number; FullName: string }[];
    ReportParticipantTypes?: ReportParticipantType[];
    Transcriptionists?: { ID: number; FullName: string }[];
}

const WORKLIST_FILTER_DATE_FIELDS = [
    'DoB',
    'From',
    'Until',
    'ReportCreatedFrom',
    'ReportCreatedUntil',
    'ReportModifiedFrom',
    'ReportModifiedUntil',
] as const satisfies readonly (keyof WorklistFilterParams)[];

interface WorklistSortingParam {
    Priority: number;
    Column: SortableWorklistColumn;
    Desc: boolean;
}

interface WorklistPaginationParams {
    PageSize: number;
    Offset: number;
}

export { WORKLIST_FILTER_DATE_FIELDS };
export type {
    Worklist,
    WorklistColumnViewParam,
    WorklistFilterParams,
    WorklistFilterViewParam,
    WorklistPaginationParams,
    WorklistParams,
    WorklistSortingParam,
    WorklistViewParams,
};
